function randomFromInterval(min, max) {
  return Math.random() * (max - min) + min;
}

function cubicBezier(P0, P1, P2, P3, t) {
  var x = (1 - t) ** 3 * P0[0] + 3 * (1 - t) ** 2 * t * P1[0] + 3 * (1 - t) * t ** 2 * P2[0] + t ** 3 * P3[0];
  var y = (1 - t) ** 3 * P0[1] + 3 * (1 - t) ** 2 * t * P1[1] + 3 * (1 - t) * t ** 2 * P2[1] + t ** 3 * P3[1];
  return [x, y];
}

// Unified body generation with continuous posture parameter
export function generateDogBody(neckBottom, headWidth) {
  // Continuous posture parameter determines overall body orientation
  let posture = randomFromInterval(0, 1); // 0-0.35: lying, 0.35-0.65: sitting, 0.65-1: standing

  if (posture < 0.35) {
    return generateLyingBody(neckBottom, headWidth);
  } else if (posture < 0.65) {
    return generateSittingBody(neckBottom, headWidth);
  } else {
    return generateStandingBody(neckBottom, headWidth);
  }
}

// Standing pose (side view - more natural)
export function generateStandingBody(neckBottom, headWidth) {
  // Continuous body proportions (no discrete types)
  let bodyLength = randomFromInterval(80, 170);
  let bodyHeight = randomFromInterval(40, 80);

  let bodyStartY = neckBottom;
  let neckX = 0;

  // Add back curve variation
  let backCurve = randomFromInterval(-8, 12);  // Can be concave or convex
  let bellySag = randomFromInterval(0, 10);     // How much belly sags

  let body = [];

  // Side view body (elongated oval with variation)
  // Top line (back) - with curve variation
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX, bodyStartY],
      [neckX + bodyLength * 0.2, bodyStartY + backCurve * 0.3],
      [neckX + bodyLength * 0.6, bodyStartY + backCurve],
      [neckX + bodyLength, bodyStartY + randomFromInterval(0, 10)],
      t
    );
    body.push(point);
  }

  // Back side (rear)
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX + bodyLength, bodyStartY + 5],
      [neckX + bodyLength + 5, bodyStartY + bodyHeight * 0.3],
      [neckX + bodyLength + 5, bodyStartY + bodyHeight * 0.7],
      [neckX + bodyLength, bodyStartY + bodyHeight],
      t
    );
    body.push(point);
  }

  // Bottom line (belly) - with sag variation
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX + bodyLength, bodyStartY + bodyHeight],
      [neckX + bodyLength * 0.6, bodyStartY + bodyHeight + bellySag],
      [neckX + bodyLength * 0.2, bodyStartY + bodyHeight + bellySag * 0.5],
      [neckX, bodyStartY + bodyHeight - randomFromInterval(0, 10)],
      t
    );
    body.push(point);
  }

  // Chest/neck connection
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX, bodyStartY + bodyHeight - 5],
      [neckX - 5, bodyStartY + bodyHeight * 0.5],
      [neckX - 5, bodyStartY + bodyHeight * 0.3],
      [neckX, bodyStartY],
      t
    );
    body.push(point);
  }

  // Legs with more variation
  let legWidth = randomFromInterval(8, 16);
  let legHeight = randomFromInterval(35, 75);  // Much wider range
  let frontLegPos = randomFromInterval(0.15, 0.3);
  let backLegPos = randomFromInterval(0.7, 0.85);

  // Front leg (near side, visible)
  let frontLeg = generateSideLeg(
    neckX + bodyLength * frontLegPos,
    bodyStartY + bodyHeight,
    legWidth,
    legHeight,
    'front'
  );

  // Back leg (near side, visible)
  let backLeg = generateSideLeg(
    neckX + bodyLength * backLegPos,
    bodyStartY + bodyHeight,
    legWidth * randomFromInterval(0.9, 1.2),
    legHeight * randomFromInterval(0.85, 1.0),
    'back'
  );

  // Tail (curved upward)
  let tail = generateTail(
    neckX + bodyLength,
    bodyStartY + bodyHeight * 0.3,
    'standing'
  );

  return {
    body,
    legs: [frontLeg, backLeg],
    tail,
    bodyBottom: bodyStartY + bodyHeight + legHeight
  };
}

// Sitting pose (side view - dog sitting upright)
export function generateSittingBody(neckBottom, headWidth) {
  // Continuous body proportions
  let bodyWidth = randomFromInterval(45, 90);
  let bodyHeight = randomFromInterval(70, 120);
  let bodyStartY = neckBottom;
  let neckX = 0;

  // Sitting lean angle
  let leanForward = randomFromInterval(-10, 15);

  let body = [];

  // Sitting body (more vertical, rounded bottom)
  // Back (nearly vertical with lean)
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX, bodyStartY],
      [neckX + bodyWidth * 0.2 + leanForward * 0.5, bodyStartY + bodyHeight * 0.2],
      [neckX + bodyWidth * 0.3 + leanForward, bodyStartY + bodyHeight * 0.6],
      [neckX + bodyWidth * 0.35 + leanForward, bodyStartY + bodyHeight],
      t
    );
    body.push(point);
  }

  // Bottom (sitting on ground - width varies)
  let bottomWidth = randomFromInterval(0.3, 0.45);
  let bottomSag = randomFromInterval(5, 12);
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX + bodyWidth * bottomWidth + leanForward, bodyStartY + bodyHeight],
      [neckX + bodyWidth * 0.1, bodyStartY + bodyHeight + bottomSag],
      [neckX - bodyWidth * 0.1, bodyStartY + bodyHeight + bottomSag],
      [neckX - bodyWidth * bottomWidth, bodyStartY + bodyHeight],
      t
    );
    body.push(point);
  }

  // Chest (curves back to neck)
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [neckX - bodyWidth * 0.35, bodyStartY + bodyHeight],
      [neckX - bodyWidth * 0.3, bodyStartY + bodyHeight * 0.6],
      [neckX - bodyWidth * 0.2, bodyStartY + bodyHeight * 0.2],
      [neckX, bodyStartY],
      t
    );
    body.push(point);
  }

  // Front legs (visible, bent slightly)
  let frontLeg = generateSittingFrontLeg(
    neckX - bodyWidth * 0.15,
    bodyStartY + bodyHeight * 0.7
  );

  // Tail (to the side or wrapped)
  let tail = generateTail(
    neckX + bodyWidth * 0.3,
    bodyStartY + bodyHeight * 0.5,
    'sitting'
  );

  return {
    body,
    legs: [frontLeg],
    tail,
    bodyBottom: bodyStartY + bodyHeight + 8
  };
}

// Lying down pose
export function generateLyingBody(neckBottom, headWidth) {
  // Continuous body proportions
  let bodyWidth = randomFromInterval(headWidth * 1.2, headWidth * 2.2);
  let bodyHeight = randomFromInterval(45, 75);
  let bodyStartY = neckBottom;

  // Curl factor (how curled up the dog is)
  let curlFactor = randomFromInterval(0, 0.3);

  let body = [];

  // Lying body (flat with curve)
  // Top (back line)
  let backRise = randomFromInterval(-5, 8);
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [-headWidth * 0.3, bodyStartY],
      [-bodyWidth * 0.3 * (1 - curlFactor), bodyStartY + backRise],
      [bodyWidth * 0.3, bodyStartY + backRise],
      [bodyWidth * 0.8 * (1 - curlFactor * 0.5), bodyStartY + randomFromInterval(0, 10)],
      t
    );
    body.push(point);
  }

  // Back
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [bodyWidth * 0.8, bodyStartY + 5],
      [bodyWidth * 0.85, bodyStartY + bodyHeight * 0.3],
      [bodyWidth * 0.8, bodyStartY + bodyHeight * 0.7],
      [bodyWidth * 0.7, bodyStartY + bodyHeight],
      t
    );
    body.push(point);
  }

  // Bottom
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [bodyWidth * 0.7, bodyStartY + bodyHeight],
      [bodyWidth * 0.3, bodyStartY + bodyHeight + 5],
      [-bodyWidth * 0.3, bodyStartY + bodyHeight + 5],
      [-headWidth * 0.3, bodyStartY + bodyHeight],
      t
    );
    body.push(point);
  }

  // Front (close to head)
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [-headWidth * 0.3, bodyStartY + bodyHeight],
      [-headWidth * 0.35, bodyStartY + bodyHeight * 0.5],
      [-headWidth * 0.35, bodyStartY + bodyHeight * 0.3],
      [-headWidth * 0.3, bodyStartY],
      t
    );
    body.push(point);
  }

  // Legs (stretched out)
  let legs = generateLyingLegs(bodyStartY, bodyHeight, bodyWidth);

  let tail = generateTail(bodyWidth * 0.75, bodyStartY + bodyHeight * 0.5, 'lying');

  return {
    body,
    legs,
    tail,
    bodyBottom: bodyStartY + bodyHeight + 5
  };
}

// Generate side view leg
function generateSideLeg(x, y, width, height, type) {
  let leg = [];

  if (type === 'front') {
    // Front leg - nearly straight
    leg.push([x - width / 2, y]);
    leg.push([x - width / 2, y + height * 0.8]);
    leg.push([x - width, y + height]);  // Paw wider
    leg.push([x + width, y + height]);
    leg.push([x + width / 2, y + height * 0.8]);
    leg.push([x + width / 2, y]);
    leg.push([x - width / 2, y]);
  } else {
    // Back leg - has knee bend
    for (let t = 0; t <= 1; t += 0.1) {
      let point = cubicBezier(
        [x - width / 2, y],                    // Top back
        [x - width, y + height * 0.3],         // Knee out
        [x - width / 2, y + height * 0.7],     // Lower leg
        [x - width, y + height],               // Paw
        t
      );
      leg.push(point);
    }

    leg.push([x + width, y + height]);  // Bottom front of paw

    for (let t = 0; t <= 1; t += 0.1) {
      let point = cubicBezier(
        [x + width, y + height],
        [x + width / 2, y + height * 0.7],
        [x + width, y + height * 0.3],
        [x + width / 2, y],
        t
      );
      leg.push(point);
    }
  }

  return leg;
}

// Sitting front leg (side view)
function generateSittingFrontLeg(x, y) {
  let leg = [];
  let width = randomFromInterval(10, 14);
  let height = randomFromInterval(50, 65);

  // Bent sitting leg
  leg.push([x - width / 2, y]);
  leg.push([x - width / 2, y + height * 0.5]);
  leg.push([x - width, y + height * 0.8]);
  leg.push([x - width * 1.5, y + height]);   // Paw extended
  leg.push([x + width / 2, y + height]);
  leg.push([x + width / 2, y + height * 0.5]);
  leg.push([x + width / 2, y]);
  leg.push([x - width / 2, y]);

  return leg;
}

// Lying legs
function generateLyingLegs(bodyStartY, bodyHeight, bodyWidth) {
  let legs = [];
  let legWidth = randomFromInterval(10, 15);

  // Front legs (visible, stretched forward)
  let frontLeg = [];
  let frontY = bodyStartY + bodyHeight * 0.3;
  frontLeg.push([-bodyWidth * 0.1, frontY]);
  frontLeg.push([-bodyWidth * 0.4, frontY]);
  frontLeg.push([-bodyWidth * 0.45, frontY + legWidth]);
  frontLeg.push([-bodyWidth * 0.1, frontY + legWidth]);
  frontLeg.push([-bodyWidth * 0.1, frontY]);
  legs.push(frontLeg);

  // Back legs (stretched backward)
  let backLeg = [];
  let backY = bodyStartY + bodyHeight * 0.6;
  backLeg.push([bodyWidth * 0.6, backY]);
  backLeg.push([bodyWidth * 0.85, backY + 5]);
  backLeg.push([bodyWidth * 0.85, backY + legWidth + 5]);
  backLeg.push([bodyWidth * 0.6, backY + legWidth]);
  backLeg.push([bodyWidth * 0.6, backY]);
  legs.push(backLeg);

  return legs;
}

// Generate tail with continuous parameters (no discrete styles)
function generateTail(x, y, pose) {
  let tail = [];
  let tailLength = randomFromInterval(35, 85);
  let tailCurve = randomFromInterval(0.5, 1.5);

  // Continuous tail parameters
  let tailAngleH = randomFromInterval(-0.6, 0.6);  // Horizontal: -1 left, 1 right
  let tailAngleV = randomFromInterval(-0.9, 0.5);  // Vertical: -1 up, 1 down
  let tailBend = randomFromInterval(0.1, 0.5);     // How much tail bends

  // Calculate tail direction based on continuous parameters
  let endX = x + tailLength * tailAngleH;
  let endY = y + tailLength * tailAngleV;

  // Control points for Bézier curve
  let ctrl1X = x + tailLength * tailAngleH * 0.3 + tailBend * tailLength * (Math.random() - 0.5);
  let ctrl1Y = y + tailLength * tailAngleV * 0.3 - tailCurve * 15;

  let ctrl2X = x + tailLength * tailAngleH * 0.7 + tailBend * tailLength * 0.5;
  let ctrl2Y = y + tailLength * tailAngleV * 0.7 - tailCurve * 8;

  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [x, y],
      [ctrl1X, ctrl1Y],
      [ctrl2X, ctrl2Y],
      [endX, endY],
      t
    );
    tail.push(point);
  }

  return tail;
}
